/**
 * Two versions side by side: what was taken out of the older one (red) and
 * what is new in the newer one (green), with the list of changes to step
 * through. Read-only; closing it returns to editing.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import type { CompareChange, CompareResult } from '../api'

interface Props {
  result: CompareResult
  older: PDFDocumentProxy
  newer: PDFDocumentProxy
  olderName: string
  newerName: string
  /** The other file is the newer one after all: compare the other way. */
  onSwap: () => void
  onClose: () => void
}

type Side = 'old' | 'new'

const SCALE = 0.9

export function CompareView({ result, older, newer, olderName, newerName, onSwap, onClose }: Props) {
  const [current, setCurrent] = useState(0)
  const panes = { old: useRef<HTMLDivElement>(null), new: useRef<HTMLDivElement>(null) }
  const { changes, summary } = result

  const go = (index: number) => {
    if (!changes.length) return
    const i = (index + changes.length) % changes.length
    setCurrent(i)
    const change = changes[i]
    for (const side of ['old', 'new'] as Side[]) {
      const pane = panes[side].current
      const page = side === 'old' ? change.oldPage : change.newPage
      if (!pane || page === null) continue
      const mark = pane.querySelector<HTMLElement>(`[data-change="${change.id}"]`)
      const target = mark ?? pane.querySelector<HTMLElement>(`[data-page="${page}"]`)
      target?.scrollIntoView({ block: mark ? 'center' : 'start', behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      else if (event.key === 'F3' || (event.key === 'ArrowDown' && event.altKey)) {
        event.preventDefault()
        go(current + (event.shiftKey ? -1 : 1))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <div className="compare" role="region" aria-label="Compare">
      <header className="compare__bar">
        <strong>Compare</strong>
        <span className="compare__summary">
          {summary.identical
            ? 'The text is identical.'
            : `${summary.changes}${summary.truncated ? '+' : ''} change${summary.changes === 1 ? '' : 's'}: ` +
              `${summary.replaced} replaced, ${summary.inserted} inserted, ${summary.deleted} deleted`}
        </span>
        <button className="btn btn--small" disabled={!changes.length} onClick={() => go(current - 1)}
                title="Previous change (Shift+F3)">{'↑'} Previous</button>
        <button className="btn btn--small" disabled={!changes.length} onClick={() => go(current + 1)}
                title="Next change (F3)">Next {'↓'}</button>
        <button className="btn btn--small" onClick={onSwap}
                title="Treat the other file as the newer version">Swap older / newer</button>
        <button className="btn btn--small" onClick={onClose} title="Back to editing (Esc)">Close</button>
      </header>
      <div className="compare__body">
        <ComparePane side="old" title={`Older: ${olderName}`} doc={older} changes={changes}
                     current={changes[current]} paneRef={panes.old} onPick={(i) => go(i)} />
        <ComparePane side="new" title={`Newer: ${newerName}`} doc={newer} changes={changes}
                     current={changes[current]} paneRef={panes.new} onPick={(i) => go(i)} />
        <ol className="compare__list">
          {changes.map((change, i) => (
            <li key={change.id}>
              <button className="compare__item" data-kind={change.kind} data-current={i === current || undefined}
                      onClick={() => go(i)}>
                <span className="compare__kind">{change.kind}</span>
                {change.oldText && <del>{clip(change.oldText)}</del>}
                {change.newText && <ins>{clip(change.newText)}</ins>}
                <span className="compare__where">
                  {change.oldPage !== null ? `p. ${change.oldPage + 1}` : ''}
                  {change.oldPage !== change.newPage && change.newPage !== null ? ` → p. ${change.newPage + 1}` : ''}
                </span>
              </button>
            </li>
          ))}
          {summary.truncated && <li className="compare__more">Only the first {changes.length} changes are listed.</li>}
        </ol>
      </div>
    </div>
  )
}

const clip = (text: string) => (text.length > 120 ? text.slice(0, 117) + '…' : text)

function ComparePane({ side, title, doc, changes, current, paneRef, onPick }: {
  side: Side
  title: string
  doc: PDFDocumentProxy
  changes: CompareChange[]
  current: CompareChange | undefined
  paneRef: React.RefObject<HTMLDivElement>
  onPick: (index: number) => void
}) {
  // Marks per page: this side's changed words, and anchors where the other
  // side's words are missing from this one.
  const marks = useMemo(() => {
    const byPage = new Map<number, { change: CompareChange; index: number; rect: number[]; anchor: boolean }[]>()
    changes.forEach((change, index) => {
      for (const group of change[side]) {
        for (const rect of group.rects) {
          const list = byPage.get(group.pageIndex) ?? []
          list.push({ change, index, rect, anchor: false })
          byPage.set(group.pageIndex, list)
        }
      }
      const anchor = side === 'old' ? change.oldAnchor : change.newAnchor
      if (anchor) {
        const list = byPage.get(anchor.pageIndex) ?? []
        list.push({ change, index, rect: anchor.rect, anchor: true })
        byPage.set(anchor.pageIndex, list)
      }
    })
    return byPage
  }, [changes, side])

  return (
    <section className="compare__pane" data-side={side}>
      <h3 className="compare__title" title={title}>{title}</h3>
      <div className="compare__pages" ref={paneRef}>
        {Array.from({ length: doc.numPages }, (_, i) => (
          <ComparePage key={i} doc={doc} index={i} marks={marks.get(i) ?? []}
                       currentId={current?.id} side={side} onPick={onPick} />
        ))}
      </div>
    </section>
  )
}

function ComparePage({ doc, index, marks, currentId, side, onPick }: {
  doc: PDFDocumentProxy
  index: number
  marks: { change: CompareChange; index: number; rect: number[]; anchor: boolean }[]
  currentId: number | undefined
  side: Side
  onPick: (index: number) => void
}) {
  const holder = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)
  const [viewport, setViewport] = useState<any>(null)
  const [visible, setVisible] = useState(index < 2)

  useEffect(() => {
    void doc.getPage(index + 1).then((page) => {
      const v = page.getViewport({ scale: SCALE })
      setViewport(v)
      setSize({ w: v.width, h: v.height })
    })
  }, [doc, index])

  useEffect(() => {
    const el = holder.current
    if (!el) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVisible(true)
    }, { rootMargin: '600px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!visible || !canvas.current) return
    let task: { cancel: () => void; promise: Promise<void> } | null = null
    void doc.getPage(index + 1).then((page) => {
      const dpr = window.devicePixelRatio || 1
      const v = page.getViewport({ scale: SCALE * dpr })
      const c = canvas.current!
      c.width = Math.floor(v.width)
      c.height = Math.floor(v.height)
      task = page.render({ canvasContext: c.getContext('2d')!, viewport: v })
      task.promise.catch(() => undefined)
    })
    return () => task?.cancel()
  }, [visible, doc, index])

  return (
    <div className="compare__page" data-page={index} ref={holder}
         style={size ? { width: size.w, height: size.h } : { width: 612 * SCALE, height: 792 * SCALE }}>
      <canvas ref={canvas} style={size ? { width: size.w, height: size.h } : undefined} />
      {viewport && marks.map((mark, i) => {
        const [x0, y0, x1, y1] = viewport.convertToViewportRectangle(mark.rect)
        return (
          <button
            key={i}
            className="compare__mark"
            data-side={side}
            data-anchor={mark.anchor || undefined}
            data-current={mark.change.id === currentId || undefined}
            data-change={i === marks.findIndex((m) => m.change.id === mark.change.id) ? mark.change.id : undefined}
            title={mark.anchor ? `${side === 'old' ? 'Inserted' : 'Deleted'} here: ${clip(side === 'old' ? mark.change.newText : mark.change.oldText)}` : mark.change.kind}
            style={{
              left: Math.min(x0, x1), top: Math.min(y0, y1),
              width: Math.max(Math.abs(x1 - x0), mark.anchor ? 3 : 2), height: Math.abs(y1 - y0),
            }}
            onClick={() => onPick(mark.index)}
          />
        )
      })}
      <span className="compare__pageno">{index + 1}</span>
    </div>
  )
}
